import { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { makeBookingManagerApi } from 'services/BookingManagerApi';
import { useNotifications } from 'hooks/useNotifications';
import {
  IBankTransactionsExportRequest,
  IBankTransactionsExportPollResponse,
  EXPORT_STATUS,
} from '../types';

const POLL_INTERVAL_MS = 1000;

/**
 * Custom hook for exporting bank transactions to CSV.
 * Uses async polling pattern: trigger export -> poll for completion -> download file.
 * OWA-10235
 */
export const useExportBankTransactions = () => {
  const api = makeBookingManagerApi();
  const { showErrorNotification, showSuccessNotification } = useNotifications();

  const [isExporting, setIsExporting] = useState(false);
  const [exportUuid, setExportUuid] = useState<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollPendingRef = useRef(false);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Trigger mutation
  const triggerMutation = useMutation({
    mutationKey: ['bank-transactions-export-trigger'],
    mutationFn: (payload: IBankTransactionsExportRequest) => api.triggerBankTransactionsExport(payload),
  });

  // Handle successful download
  const handleDownload = useCallback((signedUrl: string) => {
    // Format timestamp: colons replaced with hyphens for filesystem compatibility
    const timestamp = new Date().toISOString().replace(/:/g, '-');
    const filename = `${timestamp}-bankTransactions.csv`;
    saveAs(signedUrl, filename);
  }, []);

  // Reset export state
  const resetExport = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsExporting(false);
    setExportUuid(null);
    isPollPendingRef.current = false;
  }, []);

  // Poll for export completion
  const startPolling = useCallback(
    (uuid: string) => {
      pollIntervalRef.current = setInterval(async () => {
        // Skip if a poll request is already in flight
        if (isPollPendingRef.current) return;

        isPollPendingRef.current = true;

        try {
          const response = await api.pollBankTransactionsExport(uuid);

          // IMPORTANT: Status code is in response.data.status, NOT HTTP status
          // HTTP status is always 200, the actual status is in the body
          const { status: bodyStatus, data } = response.data as IBankTransactionsExportPollResponse;

          if (bodyStatus === EXPORT_STATUS.COMPLETE && data?.signedUrl) {
            handleDownload(data.signedUrl);
            showSuccessNotification('Export complete. Your download should begin shortly.');
            resetExport();
          } else if (bodyStatus === EXPORT_STATUS.FAILED) {
            showErrorNotification(data?.error || 'Export failed');
            resetExport();
          } else if (bodyStatus === EXPORT_STATUS.NOT_FOUND) {
            showErrorNotification('Export request not found');
            resetExport();
          }
          // If EXPORT_STATUS.PROCESSING (202), continue polling (do nothing)
        } catch (error) {
          showErrorNotification('Failed to export bank transactions');
          resetExport();
        } finally {
          isPollPendingRef.current = false;
        }
      }, POLL_INTERVAL_MS);
    },
    [api, handleDownload, resetExport, showErrorNotification, showSuccessNotification]
  );

  // Main export function
  const exportBankTransactions = useCallback(
    async (payload: IBankTransactionsExportRequest) => {
      setIsExporting(true);

      try {
        const { data } = await triggerMutation.mutateAsync(payload);
        setExportUuid(data.exportUuid);
        startPolling(data.exportUuid);
      } catch (error) {
        showErrorNotification('Failed to start export');
        resetExport();
      }
    },
    [triggerMutation, startPolling, resetExport, showErrorNotification]
  );

  return {
    exportBankTransactions,
    isExporting,
    cancelExport: resetExport,
  };
};
